import { type ZoneSource, ZoneSourceKind } from '@features/fields/api/useZoneTuning'
import { useState } from 'react'
import { PanoramaSelectMenu } from 'shared-ui/components'
import { useCffForPlanningField, useMultiStreamApplications } from 'shared-ui/field-data'
import { useLocalization } from 'shared-ui/localization'
import {
  SoilSurveyAttribute,
  useSoilAnalyteLabel,
  useSoilDatasetTitle,
  useSoilSampleDatasets
} from 'shared-ui/map'
import { useAllStreams } from 'shared-ui/operation-wide-data'
import { Enum, uniqueBy } from 'utils'

enum ZoneSourceLayerKey {
  OrganicMatter = 'omatter',
  Cec = 'cec',
  Elevation = 'elevation',
  DryYield = 'harvestdryyield',
  DryWeight = 'harvestdrymass',
  DryTons = 'harvestdrytons',
  SacksYield = 'harvestsacks',
  FurrowMoisture = 'germmoisture',
  GrainMoisture = 'grainmoisture',
  WeedDensity = 'weeddensity',
  WeedHeight = 'weedheight',
  CropDensity = 'cropdensity',
  CropHeight = 'cropheight'
}

const ZONE_SOURCE_LAYER_KEYS: ReadonlySet<string> = new Set(Object.values(ZoneSourceLayerKey))

type PassLayerSource = Extract<ZoneSource, { kind: ZoneSourceKind.AsApplied }>
type SoilSampleSource = Extract<ZoneSource, { kind: ZoneSourceKind.SoilSample }>
type SoilSurveySource = Extract<ZoneSource, { kind: ZoneSourceKind.SoilSurvey }>

type LayerOption = PassLayerSource & { id: string; name: string }

const cropSeasonIdOf = (stream: { season: number; cropKey: string }) =>
  `${stream.season}|${stream.cropKey}`

const useCropSeasonStreams = ({
  fieldId,
  sourceStreamId
}: {
  fieldId: string
  sourceStreamId: string | undefined
}) => {
  const cff = useCffForPlanningField(fieldId)
  const allStreams = useAllStreams().data ?? []
  const fieldStreams =
    cff === undefined
      ? []
      : allStreams.filter(
          (stream) =>
            stream.client === cff.client && stream.farm === cff.farm && stream.field === cff.field
        )

  const cropSeasons = fieldStreams
    .map((stream) => ({
      id: cropSeasonIdOf(stream),
      name: `${stream.season} | ${stream.cropLabel}`,
      season: stream.season,
      cropKey: stream.cropKey
    }))
    .filter(uniqueBy((a, b) => a.id === b.id))
    .toSorted((a, b) => b.season - a.season || a.name.localeCompare(b.name))

  const sourceStream = fieldStreams.find((stream) => stream.streamId === sourceStreamId)
  const sourceSeasonId = sourceStream === undefined ? undefined : cropSeasonIdOf(sourceStream)
  const [chosenId, setCropSeasonId] = useState<string>()
  const cropSeasonId = chosenId ?? sourceSeasonId
  const cropSeason = cropSeasons.find(({ id }) => id === cropSeasonId) ?? cropSeasons.at(0)
  const streams = fieldStreams.filter(
    (stream) => stream.season === cropSeason?.season && stream.cropKey === cropSeason.cropKey
  )

  return { cropSeasons, cropSeason, setCropSeasonId, streams }
}

const PassLayerPicker = ({
  fieldId,
  source,
  onSourceChange
}: {
  fieldId: string
  source: PassLayerSource | undefined
  onSourceChange: (source: ZoneSource | undefined) => void
}) => {
  const { t } = useLocalization()
  const { cropSeasons, cropSeason, setCropSeasonId, streams } = useCropSeasonStreams({
    fieldId,
    sourceStreamId: source?.streamId
  })
  const applications = useMultiStreamApplications(streams.map(({ streamId }) => ({ streamId })))

  const layerOptions: LayerOption[] = streams.flatMap((stream) => {
    const passLabel =
      stream.passName === undefined ? stream.taskLabel : `${stream.taskLabel}: ${stream.passName}`
    const streamApplications =
      applications.find(({ streamId }) => streamId === stream.streamId)?.data ?? []
    return streamApplications.flatMap((application) =>
      application.layers
        .filter((layer) => ZONE_SOURCE_LAYER_KEYS.has(layer.key))
        .map((layer) => ({
          kind: ZoneSourceKind.AsApplied as const,
          id: `${stream.streamId}/${application.application_id}/${layer.id}`,
          name: `${passLabel} · ${application.name} · ${layer.name}`,
          streamId: stream.streamId,
          applicationId: application.application_id,
          layerId: layer.id
        }))
    )
  })

  const applicationsLoaded = applications.every(({ data }) => data !== undefined)
  const noLayers = applicationsLoaded && layerOptions.length === 0
  const selectedLayer = layerOptions.find(
    (option) =>
      option.streamId === source?.streamId &&
      option.applicationId === source.applicationId &&
      option.layerId === source.layerId
  )

  return (
    <>
      <PanoramaSelectMenu
        label={t(($) => $.agConcepts.season)}
        size="fill"
        value={cropSeason}
        options={cropSeasons}
        onChange={(next) => {
          setCropSeasonId(next.id)
          onSourceChange(undefined)
        }}
      />
      <PanoramaSelectMenu
        label={t(($) => $.layer)}
        size="fill"
        value={selectedLayer}
        options={layerOptions}
        disabled={noLayers}
        onChange={({ kind, streamId, applicationId, layerId }) =>
          onSourceChange({ kind, streamId, applicationId, layerId })
        }
      />
      {noLayers && (
        <p className="pan-detail text-gray-500">{t(($) => $.planning.zoneTuning.noLayers)}</p>
      )}
    </>
  )
}

const SoilSamplePicker = ({
  fieldId,
  source,
  onSourceChange
}: {
  fieldId: string
  source: SoilSampleSource | undefined
  onSourceChange: (source: ZoneSource | undefined) => void
}) => {
  const { t, locale } = useLocalization()
  const datasets = useSoilSampleDatasets(fieldId)
  const datasetTitle = useSoilDatasetTitle()
  const analyteLabel = useSoilAnalyteLabel()
  const [chosenId, setDatasetId] = useState<string>()

  const datasetOptions = (datasets.data ?? []).map((dataset) => ({
    id: dataset.id,
    name: datasetTitle(dataset),
    analytes: dataset.analytes
  }))
  const datasetId = chosenId ?? source?.samplingEventId
  const dataset = datasetOptions.find(({ id }) => id === datasetId) ?? datasetOptions.at(0)

  const analyteOptions = (dataset?.analytes ?? [])
    .map((descriptor) => ({ id: descriptor.key, name: analyteLabel(descriptor) }))
    .toSorted((a, b) => a.name.localeCompare(b.name, locale))
  const selectedAnalyte =
    source?.samplingEventId === dataset?.id
      ? analyteOptions.find(({ id }) => id === source?.measurement)
      : undefined

  return (
    <>
      <PanoramaSelectMenu
        label={t(($) => $.planning.zoneTuning.sampling)}
        size="fill"
        value={dataset}
        options={datasetOptions}
        onChange={(next) => {
          setDatasetId(next.id)
          onSourceChange(undefined)
        }}
      />
      <PanoramaSelectMenu
        label={t(($) => $.layer)}
        size="fill"
        value={selectedAnalyte}
        options={analyteOptions}
        onChange={(next) => {
          if (dataset === undefined) return
          onSourceChange({
            kind: ZoneSourceKind.SoilSample,
            samplingEventId: dataset.id,
            measurement: next.id
          })
        }}
      />
    </>
  )
}

const SoilSurveyPicker = ({
  source,
  onSourceChange
}: {
  source: SoilSurveySource | undefined
  onSourceChange: (source: ZoneSource | undefined) => void
}) => {
  const { t } = useLocalization()
  const attributeOptions = Enum.values(SoilSurveyAttribute).map((attribute) => ({
    id: attribute,
    name: t(($) => $.map.terrain.soilSurveyAttributes[attribute])
  }))

  return (
    <PanoramaSelectMenu
      label={t(($) => $.layer)}
      size="fill"
      value={attributeOptions.find(({ id }) => id === source?.attribute)}
      options={attributeOptions}
      onChange={({ id }) => onSourceChange({ kind: ZoneSourceKind.SoilSurvey, attribute: id })}
    />
  )
}

export const ZoneSourcePicker = ({
  fieldId,
  source,
  onSourceChange
}: {
  fieldId: string
  source: ZoneSource | undefined
  onSourceChange: (source: ZoneSource | undefined) => void
}) => {
  const { t } = useLocalization()
  const datasets = useSoilSampleDatasets(fieldId)
  const [kind, setKind] = useState(source?.kind ?? ZoneSourceKind.AsApplied)

  const shouldOfferSoilSamples = !datasets.isSuccess || datasets.data.length > 0
  const kindLabels: Record<ZoneSourceKind, string> = {
    [ZoneSourceKind.AsApplied]: t(($) => $.planning.zoneTuning.asAppliedPass),
    [ZoneSourceKind.SoilSample]: t(($) => $.map.terrain.soilSamples),
    [ZoneSourceKind.SoilSurvey]: t(($) => $.map.terrain.soilSurvey)
  }
  const kindOptions = Enum.values(ZoneSourceKind)
    .filter((option) => option !== ZoneSourceKind.SoilSample || shouldOfferSoilSamples)
    .map((option) => ({ id: option, name: kindLabels[option] }))

  return (
    <div className="flex flex-col gap-3">
      <PanoramaSelectMenu
        label={t(($) => $.planning.zoneTuning.source)}
        size="fill"
        value={kindOptions.find(({ id }) => id === kind)}
        options={kindOptions}
        onChange={({ id }) => {
          setKind(id)
          onSourceChange(undefined)
        }}
      />
      {kind === ZoneSourceKind.AsApplied && (
        <PassLayerPicker
          fieldId={fieldId}
          source={source?.kind === ZoneSourceKind.AsApplied ? source : undefined}
          onSourceChange={onSourceChange}
        />
      )}
      {kind === ZoneSourceKind.SoilSample && (
        <SoilSamplePicker
          fieldId={fieldId}
          source={source?.kind === ZoneSourceKind.SoilSample ? source : undefined}
          onSourceChange={onSourceChange}
        />
      )}
      {kind === ZoneSourceKind.SoilSurvey && (
        <SoilSurveyPicker
          source={source?.kind === ZoneSourceKind.SoilSurvey ? source : undefined}
          onSourceChange={onSourceChange}
        />
      )}
    </div>
  )
}

export const useZoneSourceName = ({
  fieldId,
  source
}: {
  fieldId: string
  source: ZoneSource | undefined
}): string | undefined => {
  const { t } = useLocalization()
  const allStreams = useAllStreams().data ?? []
  const passSource = source?.kind === ZoneSourceKind.AsApplied ? source : undefined
  const [applications] = useMultiStreamApplications(
    passSource === undefined ? [] : [{ streamId: passSource.streamId }]
  )
  const datasets = useSoilSampleDatasets(fieldId)
  const datasetTitle = useSoilDatasetTitle()
  const analyteLabel = useSoilAnalyteLabel()

  if (source === undefined) return undefined

  if (source.kind === ZoneSourceKind.SoilSurvey) {
    return `${t(($) => $.map.terrain.soilSurvey)} · ${t(($) => $.map.terrain.soilSurveyAttributes[source.attribute])}`
  }

  if (source.kind === ZoneSourceKind.SoilSample) {
    const dataset = datasets.data?.find(({ id }) => id === source.samplingEventId)
    const analyte = dataset?.analytes.find(({ key }) => key === source.measurement)
    if (dataset === undefined || analyte === undefined) return undefined
    return `${datasetTitle(dataset)} · ${analyteLabel(analyte)}`
  }

  const stream = allStreams.find(({ streamId }) => streamId === source.streamId)
  const application = applications?.data?.find(
    ({ application_id }) => application_id === source.applicationId
  )
  const layer = application?.layers.find(({ id }) => id === source.layerId)
  if (stream === undefined || application === undefined || layer === undefined) return undefined
  const passLabel =
    stream.passName === undefined ? stream.taskLabel : `${stream.taskLabel}: ${stream.passName}`
  return `${stream.season} ${stream.cropLabel} · ${passLabel} · ${layer.name}`
}
